import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Subscription, forkJoin, of, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';

// Import Angular Material modules
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FilterDialogComponent } from '../../global-components/filter-dialog/filter-dialog.component';

@Component({
  selector: 'app-entity-management',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatSelectModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatDialogModule
  ],
  templateUrl: './entity-management.component.html',
  styleUrls: ['./entity-management.component.scss']
})
export class EntityManagementComponent implements OnInit, OnDestroy {
  @Input() data: any;
  @Output() recordSelected = new EventEmitter<any>();

    records: any[] = [];
    selectedRecord: any = null;
    loading = true;
    currentFilters: any = {};
    selection = new Set<any>();
    allSelected = false;
    headerTitle: string = '';
    sortColumn: string | null = null;
    sortDirection: 'asc' | 'desc' = 'asc';
    
    // Column resizing properties
    columnWidths: (number | undefined)[] = [];
    private resizingColumnIndex: number = -1;
    private startX: number = 0;
    private startWidth: number = 0;
    private mouseMoveListener!: () => void;
    private mouseUpListener!: () => void;

    private filterSub?: Subscription;
    private refreshSub?: Subscription;
    private dropdownKeys: string[] = [];

    constructor(
      private http: HttpClient,
      private configService: ConfigService,
      private globalFilterService: GlobalFilterService,
      private renderer: Renderer2,
      private dialog: MatDialog
    ) {}

    ngOnInit(): void {
      if (this.data?.header?.title) {
        this.headerTitle = this.data.header.title;
      }
    
      if (this.data?.header?.dropdowns) {
        this.dropdownKeys = this.data.header.dropdowns.map((d: any) => d.key);
        this.data.header.dropdowns.forEach((dropdown: any) => {
          if (dropdown.defaultValue) {
            this.currentFilters[dropdown.key] = dropdown.defaultValue;
          }
          if (dropdown.key === 'entity' && dropdown.defaultValue) {
            this.headerTitle = dropdown.defaultValue;
          }
        });
      }
  
      const initObservables: {[key: string]: Observable<any>} = {
        mainData: this.loadRecords()
      };

      if (this.data?.header?.dropdowns) {
        this.data.header.dropdowns.forEach((dropdown: any, index: number) => {
          if (dropdown.options?.apiUrl) {
            initObservables[`dropdown${index}`] = this.fetchDropdownOptions(dropdown);
          }
        });
      }

      forkJoin(initObservables).subscribe({
          error: (err) => {
              console.error("Initialization failed:", err);
              this.loading = false;
          }
      });
  
      this.setupFilterListener();
      this.setupRefreshListener();
    }

    fetchDropdownOptions(dropdown: any): Observable<any> {
      const { apiUrl, key } = dropdown.options;
      return this.http.get(apiUrl).pipe(map((res: any) => {
          const optionsArray = key ? res[key] : res;
          dropdown.options = Array.isArray(optionsArray) ? optionsArray : [];
          return dropdown;
      }));
    }
    
    setupFilterListener(): void {
      this.filterSub = this.globalFilterService.filter$.subscribe((filters) => {
        if (!filters || !filters.widgetId || filters.widgetId === this.data.widgetId) {
          return; // Ignore updates from itself or those without a widgetId
        }
        const newFilters = filters.requestParams || filters.queryParams || {};
        this.currentFilters = { ...this.currentFilters, ...newFilters };
        this.loadRecords().subscribe();
      });
    }

    setupRefreshListener(): void {
      this.refreshSub = this.globalFilterService.widgetRefresh$.subscribe((widgetId) => {
        if (widgetId === this.data.widgetId) {
          this.loadRecords().subscribe();
        }
      });
    }

    ngOnDestroy(): void {
      this.filterSub?.unsubscribe();
      this.refreshSub?.unsubscribe();
      this.stopResizing(); // Clean up listeners if component is destroyed during resize
    }

    loadRecords(): Observable<any[]> {
      this.loading = true;
      if (this.data?.widgetId) {
        this.globalFilterService.updateFilterSelection('loading', [this.loading], false, this.data.widgetId);
      }
      const selectedRecordId = this.selectedRecord ? this.selectedRecord.id : null;

      if (Array.isArray(this.data)) {
        this.records = this.data;
        if (this.records.length > 0) {
          this.selectRecord(this.records[0]);
        }
        this.loading = false;
        return of(this.records);
      }
      
      if (!this.data?.apiUrl) {
        this.records = [];
        this.loading = false;
        if (this.data?.widgetId) {
          this.globalFilterService.updateFilterSelection('loading', [this.loading], false, this.data.widgetId);
        }
        return of([]);
      }
  
      const method = this.data?.method?.toLowerCase() || 'post';
      let filtersToSend: any = {};
  
      if (method === 'get') {
        if (this.data.queryParamsKeys && Array.isArray(this.data.queryParamsKeys)) {
          this.data.queryParamsKeys.forEach((key: string) => {
            if (this.currentFilters[key]) {
              filtersToSend[key] = this.currentFilters[key];
            }
          });
        } else {
          filtersToSend = { ...this.currentFilters };
        }
        filtersToSend = { queryParams: filtersToSend };
      } else {
        filtersToSend = { requestParams: { ...this.currentFilters } };
      }
  
      return this.configService.getApi(this.data.apiUrl, filtersToSend, this.data, method).pipe(
        map((res: any) => {
          const key = this.data?.key || null;
          const result = key ? res?.[key] : res;
          const recordsResult =
            Array.isArray(result) ? result :
            Array.isArray(result?.data) ? result.data :
            Array.isArray(res?.data) ? res.data :
            [];

          this.records = recordsResult;
          
          if (!this.data.columns && this.records.length > 0) {
            const firstRecord = this.records[0];
            const newColumns = Object.keys(firstRecord)
              .filter(key => key !== 'id')
              .map(key => ({
                key: key,
                label: key.charAt(0).toUpperCase() + key.slice(1),
                sortable: true
              }));
            this.data.columns = newColumns;
            
            // Initialize columnWidths with default values
            this.columnWidths = [40]; // Checkbox column
            newColumns.forEach(() => this.columnWidths.push(150)); // Default width for data columns
          }

          if (this.records.length > 0) {
            let recordToSelect = this.records[0]; // Default to first record
            if (selectedRecordId) {
              const reselectedRecord = this.records.find(r => r.id === selectedRecordId);
              if (reselectedRecord) {
                recordToSelect = reselectedRecord;
              }
            }
            this.selectRecord(recordToSelect);
          } else {
            this.selectRecord(null); // Clear selection if no records
          }
          
          this.loading = false;
          if (this.data?.widgetId) {
            this.globalFilterService.updateFilterSelection('loading', [this.loading], false, this.data.widgetId);
          }
          return this.records;
        }),
        catchError((error) => {
          console.error('Error loading records:', error);
          this.loading = false;
          this.records = [];
          if (this.data?.widgetId) {
            this.globalFilterService.updateFilterSelection('loading', [this.loading], false, this.data.widgetId);
          }
          return of([]); // Return an empty observable to complete the stream
        })
      );
    }

    onResizeStart(event: MouseEvent, index: number): void {
      event.preventDefault();
      this.resizingColumnIndex = index;
      this.startX = event.clientX;
      const th = (event.target as HTMLElement).parentElement;
      this.startWidth = th?.offsetWidth || 0;

      this.mouseMoveListener = this.renderer.listen('document', 'mousemove', this.onResizing.bind(this));
      this.mouseUpListener = this.renderer.listen('document', 'mouseup', this.stopResizing.bind(this));
    }

    onResizing(event: MouseEvent): void {
      if (this.resizingColumnIndex > -1) {
        const deltaX = event.clientX - this.startX;
        const newWidth = this.startWidth + deltaX;
        if (newWidth > 50) { // Minimum column width
          this.columnWidths[this.resizingColumnIndex] = newWidth;
        }
      }
    }

    stopResizing(): void {
      this.resizingColumnIndex = -1;
      if (this.mouseMoveListener) {
        this.mouseMoveListener();
      }
      if (this.mouseUpListener) {
        this.mouseUpListener();
      }
    }

    sortBy(columnKey: string) {
      if (this.sortColumn === columnKey) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = columnKey;
        this.sortDirection = 'asc';
      }
  
      this.records.sort((a, b) => {
        const valA = a[columnKey];
        const valB = b[columnKey];
        
        if (valA < valB) {
          return this.sortDirection === 'asc' ? -1 : 1;
        }
        if (valA > valB) {
          return this.sortDirection === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    getSortIcon(columnKey: string): string {
      if (this.sortColumn !== columnKey) {
        return 'unfold_more';
      }
      if (this.sortDirection === 'asc') {
        return 'arrow_upward';
      }
      return 'arrow_downward';
    }

  handleHeaderAction(actionId: string) {
    console.log('Header action clicked:', actionId);
    if (actionId === 'refresh') {
      this.loadRecords().subscribe();
    } else if (actionId === 'filter') {
      this.openFilterDialog();
    } else if (actionId === 'add') {
      const newRecord: any = {};
      if (this.data.columns) {
        this.data.columns.forEach((col: any) => {
          newRecord[col.key] = '';
        });
      }
      newRecord.id = null; // Special marker for a new record
      this.selectRecord(newRecord);
    }
  }

    openFilterDialog(): void {
     
  
      const dialogRef = this.dialog.open(FilterDialogComponent, {
        width: '900px',
        panelClass: 'filter-dialog-panel',
        data: { 
          filterConfigUrl: this.data.filterConfigUrl,
          selectedDeam: this.currentFilters['entity'], // Pass current entity
          tableColumns: this.data?.columns || []
        }
      });
  
      dialogRef.afterClosed().subscribe((result) => {
        if (result && result.filters && result.filters.length > 0) {
          this.currentFilters.filters = result.filters;
        } else {
          delete this.currentFilters.filters;
        }
        this.loadRecords().subscribe();
      });
    }

    toggleAllSelection() {
      this.allSelected = !this.allSelected;
      if (this.allSelected) {
        this.records.forEach(record => this.selection.add(record));
      } else {
        this.selection.clear();
      }
    }

    toggleSelection(record: any) {
      if (this.selection.has(record)) {
        this.selection.delete(record);
      } else {
        this.selection.add(record);
      }
      this.allSelected = this.selection.size === this.records.length;
    }

    selectRecord(record: any) {
      this.selectedRecord = record;
      this.recordSelected.emit(record);
      if (this.data?.widgetId) {
        // Pass an array with the record, or an empty array if the record is null
        this.globalFilterService.updateFilterSelection(this.data.widgetId, record ? [record] : [], false, this.data.widgetId);
      }
    }

    onDropdownSelectionChange(dropdownKey: string, selectedValue: any): void {
      if (dropdownKey === 'entity') {
        this.headerTitle = selectedValue;
      }

      this.currentFilters[dropdownKey] = selectedValue;
      this.data.columns = undefined; // Clear existing columns to force regeneration
      this.loadRecords().subscribe();
    
      if (this.data?.widgetId) {
          const isQueryParam = this.data?.method?.toLowerCase() === 'post';
          this.globalFilterService.updateFilterSelection(dropdownKey, [selectedValue], isQueryParam, this.data.widgetId);
      }
    }
  }
  
